import { createApi } from '@reduxjs/toolkit/query/react'
import { handleError } from '../../commons/utils/utils'
import { setUpdateCard } from '../slices/updateCardSlice'
import { generateRPCQuery, getBaseQuery } from './apiUtils'
import apiConstants from './constants'

export const updatesApi = createApi({
  reducerPath: 'updatesApi',
  refetchOnFocus: false,
  baseQuery: getBaseQuery(),
  tagTypes: [
    'UpdateCardList',
    'UpdateCardGroups',
    'UpdateCardDetails',
    'AllUpdateCards'
  ],
  endpoints: (builder) => ({
    getUpdates: builder.query({
      query: (requestParams: GetUpdatesParam) =>
        generateRPCQuery(
          apiConstants.UPDATES_RPC_PATH,
          'searchUpdateCards',
          requestParams
        ),
      providesTags: [{ type: 'UpdateCardList' }]
    }),
    getUpdateCardDetails: builder.query({
      query: (requestParams: { UpdateCardID: string }) =>
        generateRPCQuery(
          apiConstants.UPDATES_RPC_PATH,
          'getUpdateCardDetailByID',
          requestParams
        ),
      providesTags: [{ type: 'UpdateCardDetails' }],
      async onQueryStarted ({}, { dispatch, queryFulfilled }) {
        try {
          const { data } = (await queryFulfilled) as {
            data?: GetUpdateCardDetailsResponse
          }
          if (data?.result) {
            dispatch(setUpdateCard(data.result))
          }
        } catch (err: any) {
          handleError(err, dispatch)
        }
      }
    }),
    getUpdateCardTargetGroups: builder.query({
      query: (requestParams: { UpdateCardID: string; isExcluded: boolean }) =>
        generateRPCQuery(
          apiConstants.UPDATES_RPC_PATH,
          'getUpdateCardTargetGroups',
          requestParams
        ),
      providesTags: [{ type: 'UpdateCardGroups' }]
    }),
    changeUpdateCardStatus: builder.mutation({
      query: (requestParams: { UpdateCardID: string; Status: string }) =>
        generateRPCQuery(
          apiConstants.UPDATES_RPC_PATH,
          'changeUpdateCardStatus',
          requestParams
        ),
      invalidatesTags: [{ type: 'UpdateCardDetails' }]
    }),
    addUpdateCardToGroup: builder.mutation({
      query: (requestParams: {
        UpdateCardID: string
        TargetGroupID: number | string
        Excluded: boolean
      }) =>
        generateRPCQuery(
          apiConstants.UPDATES_RPC_PATH,
          'addUpdateCardToGroup',
          requestParams
        ),
      invalidatesTags: [{ type: 'UpdateCardGroups' }]
    }),
    removeUpdateCardFromGroup: builder.mutation({
      query: (requestParams: {
        UpdateCardID: string
        TargetGroupID: number | string
      }) =>
        generateRPCQuery(
          apiConstants.UPDATES_RPC_PATH,
          'removeUpdateCardFromGroup',
          requestParams
        ),
      invalidatesTags: [{ type: 'UpdateCardGroups' }]
    }),
    getAllUpdateCards: builder.query({
      query: (requestParams: GetAllUpdateCardsParam) =>
        generateRPCQuery(
          apiConstants.UPDATES_RPC_PATH,
          'getAllUpdateCards',
          requestParams
        ),
      providesTags: [{ type: 'AllUpdateCards' }]
    }),
    validateUpdateCardSteps: builder.mutation({
      query: (requestParams: UpdateCardValidationParams) =>
        generateRPCQuery(
          apiConstants.UPDATES_RPC_PATH,
          'validateUpdateCardSteps',
          requestParams
        )
    }),
    createUpdateCard: builder.mutation({
      query: (requestParams: UpdateCardParams) =>
        generateRPCQuery(
          apiConstants.UPDATES_RPC_PATH,
          'createUpdateCard',
          requestParams
        ),
      invalidatesTags: ['AllUpdateCards', 'UpdateCardList']
    }),
    editUpdateCard: builder.mutation({
      query: (requestParams: UpdateCardParams) =>
        generateRPCQuery(
          apiConstants.UPDATES_RPC_PATH,
          'editUpdateCard',
          requestParams
        ),
      invalidatesTags: ['AllUpdateCards', 'UpdateCardDetails', 'UpdateCardList']
    }),
    deleteUpdateCard: builder.mutation({
      query: (requestParams: { UpdateCardID: string }) =>
        generateRPCQuery(
          apiConstants.UPDATES_RPC_PATH,
          'deleteUpdateCard',
          requestParams
        ),
      invalidatesTags: ['AllUpdateCards', 'UpdateCardList']
    })
  })
})

export const {
  useChangeUpdateCardStatusMutation,
  useAddUpdateCardToGroupMutation,
  useRemoveUpdateCardFromGroupMutation,
  useGetUpdateCardDetailsQuery,
  useLazyGetUpdateCardDetailsQuery,
  useGetUpdateCardTargetGroupsQuery,
  useGetUpdatesQuery,
  useGetAllUpdateCardsQuery,
  useValidateUpdateCardStepsMutation,
  useCreateUpdateCardMutation,
  useEditUpdateCardMutation,
  useDeleteUpdateCardMutation
} = updatesApi
